import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './GlobalTableNew.css';
import ColumnSelectorDrawer from './ColumnSelectorDrawer';
import GlobalButton from './GlobalButton';
import GlobalPagination from './GlobalPagination';
import useResponsive from '../../Hooks/useResponsive';
import TableActionButton from './TableActionButton';
import { useDispatch } from 'react-redux';
import { downloadCSV, makeCSV } from './csvExport';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalNoPagination from './GlobalNoPagination';

const GlobalTableNoPagination = ({
  headers = [],
  rows = [],
  permanentFirstRow = {},
  permanentLastRow = {},
  rowUniqueId = 'id',
  tableState = {},
  setTableState = () => {},
  checkboxNeeded = false,
  onRowActionDrawerClick = () => {},
  actionDrawerRequired = false,
  loading = true,
  selectedRowUniqueId = null,
  showToolTip = null,
  onButtonActionClick = () => {},
  handleRefresh = null,
  showOnlyRecords = false,
  customeStyleString = '',
  addSerialNo = true,
}) => {
  const {
    sort = { field: '', direction: 'asc' },
    checkedRowIds = [],
    frozenColumns = [],
    hiddenColumns = [],
  } = tableState;

  const safeRows = useMemo(() => {
    if (addSerialNo) {
      return Array.isArray(rows) ? rows.map((row, index) => ({ ...row, sl_no: index + 1 })) : [];
    }
    return Array.isArray(rows) ? rows : [];
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!tableState.search) return safeRows;

    const search = tableState.search.toLowerCase();

    return safeRows.filter(
      (
        row, //each data mapped here
      ) =>
        headers.some((col) => {
          //ALLL HEADERS FETCHING AND CHECKING THE SEARCH VALUE
          const value = row?.[col.field];

          if (value === null || value === undefined) return false;

          return String(value).toLowerCase().includes(search); //IF THE SEARCH HAS VALUE IT WILL RETURN AS TRUE THEN THE FILTER GET ENTIRE ROW
        }),
    );
  }, [safeRows, tableState.search, headers]);

  const sortedRows = useMemo(() => {
    if (!tableState.sort?.field) return filteredRows;

    const { field, direction } = tableState.sort; //GETTING THE SORT NEEDED FIELD AND ITS DIRECTION ASC OR DESC

    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[field]; //IT WILL CONTAIN THE SELCTED FIELD VALUE
      const bVal = b[field]; //IT WILL CONTAIN THE SELCTED FIELD VALUE
      // return 0  → keep position
      // return 1  → move down
      // return -1 → move up
      if (aVal === bVal) return 0; //IF THE VALUES ARE SAME THEN RETURN 0 AND THE POSITION WILL NOT CHANGE

      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1; //IF THE DIRECTION IS ASC AVAL IS GREATE AND BVALUE IS SMALL THEN RETURN 1 ELSE -1 .1 MEANS AVALUE MOVE DOWN AND -1 MEANS AVALUE MOVE UP
      }

      if (direction === 'desc') {
        return aVal < bVal ? 1 : -1; //IF THE DIRECTION IS DESC AVAL IS SMALL AND BVALUE IS GREATE THEN RETURN 1 ELSE -1 .1 MEANS AVALUE MOVE DOWN AND -1 MEANS AVALUE MOVE UP
      }
      return 0;
    });

    return sorted;
  }, [filteredRows, tableState.sort]);

  const paginatedRows = useMemo(() => {
    const start = (tableState.page - 1) * tableState.pageSize; //INDEX NEEDED SO WE SUBTRACT 1 FROM PAGE

    const end = start + tableState.pageSize; //END INDEX NEEDED SO WE ADD PAGE SIZE TO START INDEX

    return [
      ...(Object.keys(permanentFirstRow).length ? [permanentFirstRow] : []),
      ...sortedRows.slice(start, end), //SLICE THE ARRAY TO GET THE PAGINATED ROWS 0-10 SO BASICALLY 11 DATA BUT SLICE EXCLUDE END INDEX SO 10 DATA
      ...(Object.keys(permanentLastRow).length ? [permanentLastRow] : []),
    ];
  }, [sortedRows, tableState.page, tableState.pageSize, permanentFirstRow, permanentLastRow]);

  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const searchInputRef = React.useRef(tableState.search || '');
  const [compactMode, setCompactMode] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const mdUp = useResponsive('up', 'md');
  const lgUp = useResponsive('up', 'lg');

  const [exportLoading, setExportLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  /* -----------------------------------------
     ORDER HEADERS (Frozen first → normal)
  ------------------------------------------ */
  const orderedHeaders = useMemo(() => {
    return [
      ...headers.filter((h) => frozenColumns.includes(h.field)), //THE FREEZEN COLUMN WILL START FIRST
      ...headers.filter((h) => !frozenColumns.includes(h.field)), //THE BALANCE ALL ARE SHOWING AFTER THE FREEZEN ONE
    ];
  }, [headers, frozenColumns]);

  /* -----------------------------------------
     SORT HANDLER
  ------------------------------------------ */
  const handleSort = useCallback(
    (field) => {
      setTableState((prev) => ({
        ...prev,
        sort: {
          field: prev.sort.direction === 'desc' && prev.sort.field === field ? '' : field,
          direction:
            prev.sort.field === field && prev.sort.direction === 'asc'
              ? 'desc'
              : prev.sort.direction && prev.sort.field === field && prev.sort.direction == 'desc'
                ? ''
                : 'asc',
        },
        page: 1,
      }));
    },
    [setTableState],
  );

  /* -----------------------------------------
     FREEZE / UNFREEZE COLUMN
  ------------------------------------------ */
  const toggleFreezeColumn = useCallback(
    (field) => {
      setTableState((prev) => ({
        ...prev,
        frozenColumns: prev.frozenColumns.includes(field) ? prev.frozenColumns.filter((f) => f !== field) : [field],
      }));
    },
    [setTableState],
  );

  const freezeColumn = useCallback(
    (field) => {
      setTableState((prev) => ({
        ...prev,
        frozenColumns: [...prev.frozenColumns, field],
      }));
    },
    [setTableState],
  );

  const unfreezeColumn = useCallback(
    (field) => {
      setTableState((prev) => ({
        ...prev,
        frozenColumns: prev.frozenColumns.filter((f) => f !== field),
      }));
    },
    [setTableState],
  );

  /* -----------------------------------------
     ROW CHECK
  ------------------------------------------ */
  const toggleRowCheck = useCallback(
    (rowId) => {
      setTableState((prev) => ({
        ...prev,
        checkedRowIds: prev.checkedRowIds.includes(rowId)
          ? prev.checkedRowIds.filter((id) => id !== rowId)
          : [...prev.checkedRowIds, rowId],
      }));
    },
    [setTableState],
  );

  /* -----------------------------------------
     SELECT ALL
  ------------------------------------------ */
  const toggleAllRows = useCallback(
    (checked) => {
      setTableState((prev) => ({
        ...prev,
        checkedRowIds: checked ? paginatedRows?.map((r) => r[rowUniqueId]) : [],
      }));
    },
    [paginatedRows, rowUniqueId, setTableState],
  );

  const allChecked = paginatedRows.length > 0 && checkedRowIds.length === paginatedRows.length;

  /* -----------------------------------------
     RENDER
  ------------------------------------------ */

  const handleSearch = () => {
    if (searchInputRef.current.value.trim() === '') return;
    setTableState((prev) => ({
      ...prev,
      search: searchInputRef.current.value.trim(),
      page: 1,
    }));
  };

  const handleClearSearch = () => {
    if (searchInputRef.current) searchInputRef.current.value = '';
    setTableState((prev) => ({
      ...prev,
      search: '',
      page: 1,
    }));
  };
  const getAlignClass = (align) => {
    switch (align) {
      case 'left':
        return 'align-left';
      case 'right':
        return 'align-right';
      default:
        return 'align-center';
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const data = safeRows || [];
      console.log('CSV MAKING START');
      const csv = makeCSV({
        headers,
        rows: data,
        excludedFields: [...hiddenColumns],
      });
      const fileName = `Export_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`;
      downloadCSV(csv, fileName);
      const csvToTSV = (csvText) => {
        return csvText
          .split('\n')
          .map((line) => line.split(',').join('\t'))
          .join('\n');
      };

      if (navigator?.clipboard?.writeText) {
        const tsv = csvToTSV(csv);
        navigator.clipboard
          .writeText(tsv)
          .then(() => console.log('Copied to clipboard ✅'))
          .catch((err) => console.log('Clipboard copy failed:', err));
      }

      enqueueSnackbar('CSV Exported successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.log('error', error);
      enqueueSnackbar('Export failed', {
        variant: 'error',
      });
    } finally {
      setExportLoading(false);
    }
  };

  const noHeaderOrRowsFound = paginatedRows.length === 0 || Object.keys(headers).length === 0;

  const getCellValue = (col, row) => {
    const rawValue = row?.[col.field];

    if (col.valueGetter) {
      let value;
      if (typeof col.valueGetter === 'function') {
        value = col.valueGetter({ row, value: rawValue });
      } else {
        value = col.valueGetter;
      }

      if (React.isValidElement(value)) {
        console.error(`❌ JSX not allowed in valueGetter: ${col.field}`);
        return '—';
      }

      // optional: prevent object also
      if (typeof value === 'object' && value !== null) {
        console.error(`❌ Object not allowed in valueGetter: ${col.field}`);
        return '—';
      }

      return value ?? '—';
    }

    return rawValue ?? '—';
  };

  const rowsMapRef = useRef(new Map());

  useEffect(() => {
    const map = new Map();
    paginatedRows.forEach((r) => map.set(String(r[rowUniqueId]), r));
    rowsMapRef.current = map;
  }, [paginatedRows, rowUniqueId]);

  const handleBodyClick = useCallback(
    (e) => {
      // console.groupCollapsed('🟦 GlobalTable Body Click');
      // console.time('buttonCliekedAndFindedAndRetunedRow');
      const btn = e.target.closest('button[data-actionkey]');

      if (!btn) {
        return;
      }
      e.stopPropagation();

      const actionKey = btn.dataset.actionkey;
      const actionType = btn.dataset.actiontype;
      const rowId = String(btn.dataset.rowid);
      const row = rowsMapRef.current.get(rowId);

      if (!row) {
        return;
      }
      onButtonActionClick(actionKey, row, actionType);
      // console.timeEnd('buttonCliekedAndFindedAndRetunedRow');
      // console.groupEnd();
    },
    [onButtonActionClick],
  );

  const capFirst = (str = '') => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');
  const getDisabled = (col, row) => {
    if (typeof col?.isDisabled === 'function') {
      return !!col.isDisabled({ row, value: row?.[col.field] });
    }
    return !!col?.isDisabled; // undefined => false
  };

  const handleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen(); // full page like F11
        setFullScreen(true);
      } else {
        await document.exitFullscreen();
        setFullScreen(false);
      }
    } catch (err) {
      console.log('Fullscreen error:', err);
    }
  };

  //CALCULATION LEFT OFFSET WHILE FREEZING AND UNFREEZING COLUMNS
  const updateFrozenOffsets = () => {
    const table = document.querySelector('.global-table');
    if (!table) return;

    const rows = table.querySelectorAll('tr');

    rows.forEach((row) => {
      let left = 0;

      const frozenCells = row.querySelectorAll('.sticky-left, .frozen-col');

      frozenCells.forEach((cell) => {
        cell.style.left = `${left}px`;
        left += cell.offsetWidth;
      });
    });
  };

  useEffect(() => {
    updateFrozenOffsets(); //ITWILL TRIGGER WHEN FROZEN COLUMNS CHANGES
    window.addEventListener('resize', updateFrozenOffsets);
    return () => window.removeEventListener('resize', updateFrozenOffsets);
  }, [
    tableState.frozenColumns,
    rows,
    checkboxNeeded,
    tableState.hiddenColumns,
    tableState.page,
    tableState?.pageSize,
    tableState?.search,
    tableState?.sort,
  ]);

  return (
    <div
      className={`
      table-wrapper
      ${compactMode ? 'compact-mode' : ''}
      ${fullScreen ? 'fullscreen-mode' : ''}
      `}
    >
      <style>{`.global-table ${customeStyleString}`}</style>

      <div
        className="table-top"
        style={{
          flexDirection: lgUp ? 'row' : 'column',
          justifyContent: lgUp ? 'space-between' : 'flex-start',
          alignItems: lgUp ? 'center' : 'flex-start',
          gap: lgUp ? '0' : '8px',
        }}
      >
        <ColumnSelectorDrawer
          open={showColumnSelector}
          onClose={() => setShowColumnSelector(false)}
          headers={headers}
          hiddenColumns={hiddenColumns}
          setTableState={setTableState}
        />

        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexDirection: mdUp ? 'row' : 'column',
            justifyContent: mdUp ? '' : 'flex-start',
            alignItems: mdUp ? 'center' : 'flex-start',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            {typeof handleRefresh === 'function' && (
              <GlobalButton
                type={'refresh'}
                size="sm"
                style={{
                  backgroundColor: '#f0f0f0',
                  color: '#040404ff',
                  borderColor: '#2563eb',
                }}
                disabled={headers?.length <= 1 || loading || exportLoading}
                label={'Refresh'}
                active={false}
                onClick={() => handleRefresh()}
              />
            )}
            <GlobalButton
              type={'columns'}
              size="sm"
              style={{
                backgroundColor: '#f0f0f0',
                color: '#040404ff',
                borderColor: '#2563eb',
              }}
              disabled={headers?.length <= 1 || loading || exportLoading || !Array.isArray(tableState?.hiddenColumns)}
              label={showColumnSelector ? 'Hide Columns' : 'Show Columns'}
              active={showColumnSelector}
              onClick={() => setShowColumnSelector((p) => !p)}
            />
            {safeRows?.length > 0 && (
              <GlobalButton
                // hideIcon={!lgUp}
                loading={exportLoading}
                size="sm"
                disabled={exportLoading || loading || noHeaderOrRowsFound}
                type={'export'}
                label={'Export'}
                onClick={handleExport}
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <GlobalButton
              // hideIcon={!lgUp}
              disabled={loading}
              size="sm"
              type={compactMode ? 'normal' : 'compact'}
              label={compactMode ? 'Normal View' : 'Compact View'}
              active={compactMode}
              onClick={() => setCompactMode((p) => !p)}
            />
            <GlobalButton
              // hideIcon={!lgUp}
              disabled={loading}
              size="sm"
              type="fullscreen"
              label={fullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
              active={fullScreen}
              // onClick={() => setFullScreen((p) => !p)}
              onClick={handleFullscreen}
            />

            <div className="top-loading">
              {loading ? (
                <Icon icon="eos-icons:bubble-loading" width="24" height="24" style={{ color: '#0a25efff' }} />
              ) : (
                ''
              )}
            </div>
          </div>
        </div>

        <div className="table-top-right">
          <input
            type="text"
            placeholder="Search"
            defaultValue={tableState.search || ''}
            ref={searchInputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            style={{
              minWidth: lgUp ? '260px' : '150px', // ✅ reduce 50px
            }}
          />

          {(tableState.search || (searchInputRef.current && searchInputRef.current.value)) && (
            <GlobalButton
              hideIcon={!lgUp}
              disabled={loading}
              size="sm"
              type={'clear'}
              label={lgUp ? 'Clear Search' : 'Clear'}
              onClick={handleClearSearch}
              className="clear-btn-icon"
            />
          )}

          <GlobalButton
            hideIcon={!lgUp}
            size="sm"
            disabled={loading}
            type={'search'}
            label={'Search'}
            onClick={() => handleSearch()}
          />
        </div>
      </div>

      <div
        className={`
    table-container
    ${loading ? 'loading' : ''}
    ${!loading && paginatedRows?.length === 0 ? 'no-data' : ''}
  `}
      >
        {loading && (
          <div className="table-loading-overlay">
            <div className="table-loading-spinner" />
          </div>
        )}

        {!loading && paginatedRows?.length === 0 && (
          <div className="no-data-overlay">
            <div className="no-data-content">
              <svg
                style={{ width: 60, height: 60, color: '#000' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              No Data Found
            </div>
          </div>
        )}
        <table className="global-table">
          <thead>
            <tr>
              {/* CHECKBOX HEADER */}
              {checkboxNeeded && (
                <th className="sticky-left checkbox-col">
                  <input
                    disabled={loading}
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => toggleAllRows(e.target.checked)}
                  />
                </th>
              )}

              {/* DATA HEADERS */}
              {orderedHeaders.map((col) =>
                !hiddenColumns.includes(col.field) ? (
                  <th
                    key={col.field}
                    // Hovering a header can explain what the column actually means.
                    // Optional: a column without headerTooltip renders no title at
                    // all, so every existing table is unaffected.
                    title={col.headerTooltip || undefined}
                    style={{ width: col.width ?? 150 }}
                    className={`
                      ${col.sortable ? 'sortable' : ''}
                     ${frozenColumns.includes(col.field) ? 'frozen-col' : ''}
                     ${col.align ? getAlignClass(col.align) : ''}
                     `}
                    onClick={() => col.sortable && handleSort(col.field)}
                  >
                    <div className="th-content">
                      <div className="th-left">
                        <button
                          disabled={loading || !Array.isArray(tableState?.frozenColumns)}
                          className="freeze-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            // toggleFreezeColumn(col.field);
                            if (frozenColumns.includes(col.field)) {
                              unfreezeColumn(col.field);
                            } else {
                              freezeColumn(col.field);
                            }
                          }}
                          title={frozenColumns.includes(col.field) ? 'Unfreeze column' : 'Freeze column'}
                        >
                          {frozenColumns.includes(col.field) ? '⛔' : '📌'}
                        </button>
                      </div>

                      {/* HEADER NAME - CENTER */}
                      <div className="th-center">
                        <span className="th-label">{col.headerName}</span>
                      </div>

                      {/* SORT ICONS - RIGHT */}
                      <div className="th-right">
                        {col.sortable && tableState?.sort && (
                          <div className="sort-icons">
                            <span className={sort.field === col.field && sort.direction === 'asc' ? 'active' : ''}>
                              ▲
                            </span>
                            <span className={sort.field === col.field && sort.direction === 'desc' ? 'active' : ''}>
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                ) : null,
              )}

              {/* ACTION COLUMN */}
              {actionDrawerRequired && <th className="sticky-right action-col"> &#9776;</th>}
            </tr>
          </thead>
          <tbody onClick={handleBodyClick}>
            {paginatedRows?.map((row) => {
              const rowId = row[rowUniqueId];

              return (
                <tr
                  key={rowId}
                  className={selectedRowUniqueId === rowId ? 'active-row' : ''}
                  title={showToolTip ? row[showToolTip] : ''}
                >
                  {/* CHECKBOX CELL */}
                  {checkboxNeeded && (
                    <td className="sticky-left checkbox-col">
                      <input
                        type="checkbox"
                        checked={checkedRowIds.includes(rowId)}
                        onChange={() => toggleRowCheck(rowId)}
                      />
                    </td>
                  )}

                  {orderedHeaders.map((col) => {
                    if (hiddenColumns.includes(col.field)) return null;
                    const tdClassName = `
                                          ${frozenColumns.includes(col.field) ? 'frozen-col' : ''}
                                          ${getAlignClass(col.align)}
                                            `;
                    const title = (() => {
                      if (!('toolTipGetter' in col)) return undefined; // ✅ only when exists

                      const tt = col.toolTipGetter;

                      if (typeof tt === 'function') return tt({ row, value: row?.[col.field] });
                      if (typeof tt === 'string') return tt;
                      if (tt === true) return row?.[col.field];

                      return undefined;
                    })();

                    // ✅ ACTION COLUMN
                    if (col.type === 'action') {
                      const disabled = getDisabled(col, row);

                      return (
                        <td key={col.field} className={tdClassName}>
                          <TableActionButton
                            actionKey={col?.actionKey}
                            rowId={rowId}
                            actionType={col?.actionType}
                            icon={col?.icon}
                            label={col?.buttonName || capFirst(col?.actionType || col?.headerName || '')}
                            disabled={disabled || loading}
                            loading={loading}
                            title={title}
                            buttonProps={col?.buttonProps}
                            // onClick={(e) => {
                            //   e.stopPropagation();
                            //   if (disabled || loading) return;
                            //   onButtonActionClick(col?.actionKey, row);
                            // }}
                          />
                        </td>
                      );
                    }

                    const value = getCellValue(col, row);
                    const cellStyle = col.cellStyle
                      ? typeof col.cellStyle === 'function'
                        ? col.cellStyle({ row, value })
                        : col.cellStyle
                      : undefined;

                    return (
                      <td key={col.field} className={tdClassName} title={title} style={cellStyle}>
                        {col.renderCell ? col.renderCell({ row, value, field: col.field }) : (value ?? '—')}
                      </td>
                    );
                  })}

                  {actionDrawerRequired && (
                    <td
                      className="sticky-right action-col"
                      onClick={(e) => {
                        e.stopPropagation(); // stop row click
                        onRowActionDrawerClick(row); // open drawer
                      }}
                      title="Row actions"
                    >
                      <button className="dots-btn" type="button">
                        ⋮
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <GlobalNoPagination
        loading={loading}
        mdUp={mdUp}
        meta={{
          totalRecords: rows?.length,
          totalPages: Math.ceil(rows?.length / tableState?.pageSize),
        }}
        showOnlyRecords={showOnlyRecords}
        tableState={tableState}
        setTableState={setTableState}
      />
    </div>
  );
};

export default memo(GlobalTableNoPagination);
